import { AbstractResultList } from "@component/assets/AbstractListingCalculator/AbstractResultList";
import { faPhone as fasPhone } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import { Button, List } from "bm-ui-kit";
import type { CommonOptions } from "bm-ui-kit";
import React from "react";
import type { ReactNode } from "react";
import { Col, Collapse, Container, Row } from "react-bootstrap";
import { CorporateBankAccountLeadForm } from "../LeadForm/CorporateBankAccountLeadForm";
import { CorporateBankAccountResultItem } from "./CorporateBankAccountResultItem";
import { getLeadCalculatorRequest, getProfileBySearchParams, renderFee } from "./domain";
import type {
	CorporateBankAccountRequest,
	CorporateBankAccountResponseItem,
	CorporateBankAccountSearchParams,
	CorporateBankAccountSort,
} from "./types";

export class CorporateBankAccountResultList extends AbstractResultList<
	CorporateBankAccountSearchParams,
	CorporateBankAccountSort,
	CorporateBankAccountRequest,
	CorporateBankAccountResponseItem
> {
	public static readonly DEFAULT_SORT: CorporateBankAccountSort = "";
	protected sortOptions: CommonOptions<CorporateBankAccountSort> = [
		{ label: "Számlavezetési díj", value: "" },
		{ label: "Bank név", value: "bankName" },
	];

	protected renderResult(props): ReactNode {
		return <CorporateBankAccountResultItem {...props} withoutNumber />;
	}

	protected renderListingOffer(): ReactNode {
		const { searchParams } = this.props;
		const { showListingOffer } = this.state;

		return (
			<div className="listing-offer-block" key="listing-offer">
				<Container className="text-center my-3">
					<div className="h1 d-inline-block mb-lg-0">Kérj személyre szabott ajánlatot</div>
					<Button
						size="lg"
						icon={<FontAwesomeIcon icon={fasPhone} />}
						className="text-uppercase ml-3"
						onClick={this.onListingOfferClick}
					>
						Tanácsadónktól
					</Button>
					<div className="d-inline-block">
						<List itemStyle="tick" variant="success" className="listing-offer-msg">
							<List.Item>
								Céged adatai alapján kiszámítjuk a{" "}
								<strong className="text-uppercase">Teljes havi bankköltséged</strong>-edet
							</List.Item>
						</List>
					</div>
				</Container>
				<Collapse in={showListingOffer}>
					<div className="fluid-block text-white mt-3">
						<Container>
							<Row className="justify-content-center align-items-center">
								<Col className="text-block" lg="7">
									<div className="h3 text-white font-weight-bold mt-3 mb-4">
										Alacsony bankköltségek minden vállalkozásnak
									</div>
									<List itemStyle="tick" variant="gold">
										<List.Item className="d-none d-md-block">
											Kérd <strong>teljeskörű tájékoztatónkat</strong> a{" "}
											<strong>legapróbb költségtételre</strong>
											<br /> kiterjedően is
										</List.Item>
										<List.Item>
											Céged adatai alapján kiszámítjuk a <strong>TELJES HAVI BANKKÖLTSÉG</strong>
											-edet
										</List.Item>
										<List.Item className="d-none d-md-block">
											Javaslatokat adunk a <strong>számlaköltségek további csökkentésére</strong>
											(pl. devizaforgalomhoz kapcsolódó költség optimalizálás)
										</List.Item>
										<List.Item className="d-none d-md-block">
											Választ adunk az <strong>egyedi igényeidre</strong> is (pl.
											folyószámlahitel)
										</List.Item>
										<List.Item>
											200 millió Ft feletti árbevételű cégeknek{" "}
											<strong>EGYEDI AJÁNLATOKAT</strong> érünk el a bankoknál
										</List.Item>
										<List.Item className="d-none d-md-block">
											<strong>Kényelmes</strong> folyamat, gyors lebonyolítás, sorban állás
											nélküli ügyintézés a Bankmonitor szakértőivel
										</List.Item>
									</List>
								</Col>
								<Col className="pb-4 pb-lg-0" lg="5" md="8">
									<CorporateBankAccountLeadForm
										{...this.getListingOfferLeadProps()}
										title="Kérj segítséget szakértőnktől!"
										request={getLeadCalculatorRequest(searchParams)}
									/>
								</Col>
							</Row>
						</Container>
					</div>
				</Collapse>
			</div>
		);
	}

	protected renderNormalListTitle(): ReactNode {
		return "Vállalati bankszámlákra a bankok a következő ajánlatokat kínálják";
	}

	protected createViewItemListEventItem(result: CorporateBankAccountResponseItem): Record<string, any> {
		const { searchParams } = this.props;
		const profile = getProfileBySearchParams(searchParams).substring(0, 9);
		const isProfile5 = ["PROFILE_5"].includes(profile);

		return {
			coupon: "none",
			currency: "HUF",
			discount: 0,
			item_category: renderFee(result.transactionFees.moneyTransferFees.huf.oneOffIntraBankElectronic),
			item_category2: renderFee(result.transactionFees.moneyTransferFees.sepaElectronic),
			item_category3: formatText(result.cardYearlyFees.electronic, "ft"),
			item_category4: formatText(result.cardYearlyFees.embossed, "ft"),
			item_category5: isProfile5 ? formatText(result.cardYearlyFees.premium, "ft") : undefined,
			item_variant: isProfile5 ? formatText(result.cardYearlyFees.internet, "ft") : undefined,
			price: result.hufAccountManagementFee + result.hufProductFee,
			quantity: ["PROFILE_2", "PROFILE_3", "PROFILE_5"].includes(profile)
				? result.currencyAccountManagementFee
				: 0,
		};
	}

	protected getComponentName(): string {
		return "CorporateBankAccountResultList";
	}
}
